/**
 * questionari/validazione — le regole delle risposte, uguali nella pagina e sul server.
 * ============================================================================
 *
 * La pagina pubblica usa `valutaRisposta` per dire SUBITO che cosa manca (il messaggio lo traduce
 * lei dal codice d'errore); il server usa `validaRisposte` e `validaContatti` per non fidarsi del
 * browser. Stesse regole, un posto solo: una regola cambiata qui vale in entrambi.
 *
 * Gli errori sono CODICI (ASCII, mai tradotti): chi mostra il messaggio sceglie la lingua.
 *
 * File puro, target ES2017.
 */

import {
  CAMPI_CHIAVE_LEAD,
  LIMITI_QUESTIONARI,
  type CampoContattoId,
  type ContattiRisposta,
  type Domanda,
  type RispostaSalvata,
  type SchermataContatti,
  type ValoreRisposta,
} from './tipi';

// ─────────────────────────────────────────────────────────────────────────────
// Una risposta
// ─────────────────────────────────────────────────────────────────────────────

export type CodiceErroreRisposta =
  /** Obbligatoria e senza risposta. */
  | 'vuota'
  /** Il valore non è del tipo che la domanda vuole (manomissione o difetto della pagina). */
  | 'tipo'
  | 'opzione_inesistente'
  | 'opzioni_duplicate'
  | 'troppe_scelte'
  | 'esclusiva_con_altre'
  /** Non un intero. */
  | 'numero_non_valido'
  | 'numero_fuori_limiti'
  | 'troppo_lunga'
  /** Il campo che compare sotto l'opzione è obbligatorio e vuoto. */
  | 'dettaglio_vuoto';

export interface EsitoRisposta {
  /** Il valore normalizzato; assente = nessuna risposta. */
  valore?: ValoreRisposta;
  dettaglio?: string;
  errore?: CodiceErroreRisposta;
}

/** Vuoto = nessuna risposta: assente, stringa di soli spazi, elenco vuoto. */
export function eVuoto(valore: unknown): boolean {
  if (valore === undefined || valore === null) return true;
  if (typeof valore === 'string' && valore.trim() === '') return true;
  if (Array.isArray(valore) && valore.length === 0) return true;
  return false;
}

function siNo(valore: unknown): 'si' | 'no' | null {
  if (valore === true) return 'si';
  if (valore === false) return 'no';
  if (typeof valore !== 'string') return null;
  const v = valore.trim().toLowerCase();
  if (v === 'si' || v === 'sì') return 'si';
  if (v === 'no') return 'no';
  return null;
}

/** Il campo di dettaglio è visibile con queste scelte? */
export function dettaglioVisibile(domanda: Domanda, scelte: string[]): boolean {
  const cd = domanda.campo_dettaglio;
  if (!cd || !Array.isArray(cd.mostra_se)) return false;
  return scelte.some((s) => cd.mostra_se.indexOf(s) >= 0);
}

/**
 * Valuta UNA risposta rispetto alla sua domanda, obbligatorietà compresa.
 * Per le scelte il valore è l'id (o l'elenco di id) delle opzioni.
 */
export function valutaRisposta(domanda: Domanda, valore: unknown, dettaglio?: unknown): EsitoRisposta {
  if (eVuoto(valore)) return domanda.obbligatoria ? { errore: 'vuota' } : {};

  switch (domanda.tipo) {
    case 'testo_breve':
    case 'testo_lungo': {
      if (typeof valore !== 'string') return { errore: 'tipo' };
      if (valore.length > LIMITI_QUESTIONARI.max_testo) return { errore: 'troppo_lunga' };
      return { valore: valore.trim() };
    }
    case 'scelta_singola': {
      const ids = (domanda.opzioni ?? []).map((o) => o.id);
      if (typeof valore !== 'string') return { errore: 'tipo' };
      if (ids.indexOf(valore) < 0) return { errore: 'opzione_inesistente' };
      return conDettaglio(domanda, valore, [valore], dettaglio);
    }
    case 'scelta_multipla': {
      if (!Array.isArray(valore) || valore.length > LIMITI_QUESTIONARI.max_selezioni) return { errore: 'tipo' };
      const opzioni = domanda.opzioni ?? [];
      const ids = opzioni.map((o) => o.id);
      const scelte: string[] = [];
      for (const s of valore) {
        if (typeof s !== 'string') return { errore: 'tipo' };
        if (ids.indexOf(s) < 0) return { errore: 'opzione_inesistente' };
        if (scelte.indexOf(s) >= 0) return { errore: 'opzioni_duplicate' };
        scelte.push(s);
      }
      if (domanda.massimo_scelte !== undefined && scelte.length > domanda.massimo_scelte) return { errore: 'troppe_scelte' };
      const esclusive = opzioni.filter((o) => o.esclusiva).map((o) => o.id);
      if (scelte.length > 1 && scelte.some((s) => esclusive.indexOf(s) >= 0)) return { errore: 'esclusiva_con_altre' };
      // L'ordine delle opzioni, non quello dei clic: le risposte si confrontano meglio.
      const ordinate = ids.filter((id) => scelte.indexOf(id) >= 0);
      return conDettaglio(domanda, ordinate, ordinate, dettaglio);
    }
    case 'scala_1_10': {
      const n = typeof valore === 'string' ? Number(valore) : valore;
      if (typeof n !== 'number' || !Number.isInteger(n) || n < 1 || n > 10) return { errore: 'numero_non_valido' };
      return { valore: n };
    }
    case 'si_no': {
      const v = siNo(valore);
      return v === null ? { errore: 'tipo' } : { valore: v };
    }
    case 'numero': {
      const n = typeof valore === 'string' && /^\s*\d+\s*$/.test(valore) ? Number(valore) : valore;
      if (typeof n !== 'number' || !Number.isInteger(n)) return { errore: 'numero_non_valido' };
      const minimo = domanda.minimo ?? 0;
      const massimo = domanda.massimo ?? LIMITI_QUESTIONARI.max_numero;
      if (n < minimo || n > massimo) return { errore: 'numero_fuori_limiti' };
      return { valore: n };
    }
    default:
      return { errore: 'tipo' };
  }
}

function conDettaglio(domanda: Domanda, valore: ValoreRisposta, scelte: string[], dettaglio: unknown): EsitoRisposta {
  if (!dettaglioVisibile(domanda, scelte)) return { valore };
  const testo = typeof dettaglio === 'string' ? dettaglio.trim() : '';
  if (testo.length > LIMITI_QUESTIONARI.max_campo_contatto) return { errore: 'troppo_lunga' };
  if (!testo) return domanda.campo_dettaglio!.obbligatorio ? { errore: 'dettaglio_vuoto' } : { valore };
  return { valore, dettaglio: testo };
}

// ─────────────────────────────────────────────────────────────────────────────
// Tutte le risposte
// ─────────────────────────────────────────────────────────────────────────────

function corrispondeAlSalto(domanda: Domanda, valore: ValoreRisposta, atteso: string): boolean {
  if (domanda.tipo === 'si_no') return siNo(valore) !== null && siNo(valore) === siNo(atteso);
  const a = atteso.trim();
  if (Array.isArray(valore)) return valore.indexOf(a) >= 0;
  return String(valore) === a;
}

/**
 * Le domande sul percorso che la persona vede davvero, seguendo i salti. Solo salti in avanti:
 * un arrivo inesistente o all'indietro si ignora, così il percorso non può girare in tondo.
 */
export function percorsoVisibile(domande: Domanda[], valori: { [domandaId: string]: ValoreRisposta | undefined }): string[] {
  const indice: { [id: string]: number } = {};
  domande.forEach((d, i) => {
    indice[d.id] = i;
  });
  const visibili: string[] = [];
  let i = 0;
  while (i < domande.length) {
    const d = domande[i];
    visibili.push(d.id);
    let prossima = i + 1;
    const v = valori[d.id];
    if (d.salta_a && v !== undefined && corrispondeAlSalto(d, v, d.salta_a.se_risposta)) {
      const arrivo = indice[d.salta_a.vai_a_domanda_id];
      if (arrivo !== undefined && arrivo > i) prossima = arrivo;
    }
    i = prossima;
  }
  return visibili;
}

export type CodiceErroreInvio = CodiceErroreRisposta | 'domanda_inesistente' | 'risposta_duplicata' | 'formato';

export interface ErroreInvio {
  domanda_id: string | null;
  codice: CodiceErroreInvio;
}

/**
 * Valida un invio completo. Restituisce SOLO le risposte sul percorso visibile, normalizzate e
 * nell'ordine delle domande; le obbligatorie fuori percorso non contano.
 */
export function validaRisposte(
  domande: Domanda[],
  input: Array<{ domanda_id: unknown; valore: unknown; dettaglio?: unknown }>,
): { risposte: RispostaSalvata[]; errori: ErroreInvio[] } {
  const errori: ErroreInvio[] = [];
  const perId: { [id: string]: Domanda } = {};
  domande.forEach((d) => {
    perId[d.id] = d;
  });

  const valori: { [id: string]: ValoreRisposta | undefined } = {};
  const dettagli: { [id: string]: string | undefined } = {};
  const errorePerDomanda: { [id: string]: CodiceErroreRisposta } = {};
  const visti: { [id: string]: true } = {};

  for (const r of input) {
    if (!r || typeof r.domanda_id !== 'string') {
      errori.push({ domanda_id: null, codice: 'formato' });
      continue;
    }
    const d = perId[r.domanda_id];
    if (!d) {
      errori.push({ domanda_id: r.domanda_id, codice: 'domanda_inesistente' });
      continue;
    }
    if (visti[d.id]) {
      errori.push({ domanda_id: d.id, codice: 'risposta_duplicata' });
      continue;
    }
    visti[d.id] = true;
    // L'obbligatorietà si guarda dopo, sul percorso: qui conta solo la forma del valore.
    const esito = valutaRisposta({ ...d, obbligatoria: false }, r.valore, r.dettaglio);
    if (esito.errore) errorePerDomanda[d.id] = esito.errore;
    else if (esito.valore !== undefined) {
      valori[d.id] = esito.valore;
      if (esito.dettaglio) dettagli[d.id] = esito.dettaglio;
    }
  }

  const visibili = percorsoVisibile(domande, valori);
  const risposte: RispostaSalvata[] = [];
  for (const d of domande) {
    if (visibili.indexOf(d.id) < 0) continue;
    if (errorePerDomanda[d.id]) {
      errori.push({ domanda_id: d.id, codice: errorePerDomanda[d.id] });
      continue;
    }
    const v = valori[d.id];
    if (v === undefined) {
      if (d.obbligatoria) errori.push({ domanda_id: d.id, codice: 'vuota' });
      continue;
    }
    const riga: RispostaSalvata = { domanda_id: d.id, valore: v };
    if (dettagli[d.id]) riga.dettaglio = dettagli[d.id];
    risposte.push(riga);
  }
  return { risposte, errori };
}

// ─────────────────────────────────────────────────────────────────────────────
// Ultima schermata (contatti)
// ─────────────────────────────────────────────────────────────────────────────

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * La partita IVA come chiave: maiuscolo, solo lettere e cifre, e senza il prefisso «IT» davanti
 * alle 11 cifre italiane (prima «IT01234567890» e «01234567890» erano due aziende diverse).
 * Le partite IVA estere (8-13 caratteri) restano col loro prefisso.
 */
export function normalizzaPartitaIva(grezza: unknown): string | null {
  if (typeof grezza !== 'string') return null;
  let p = grezza.trim().toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (/^IT\d{11}$/.test(p)) p = p.slice(2);
  if (p.length < 8 || p.length > 13) return null;
  return p;
}

/** L'email come chiave: senza spazi e in minuscolo. */
export function normalizzaEmail(grezza: unknown): string | null {
  if (typeof grezza !== 'string') return null;
  const e = grezza.trim().toLowerCase();
  if (!e || e.length > LIMITI_QUESTIONARI.max_email || !EMAIL_RE.test(e)) return null;
  return e;
}

/** Un numero di telefono plausibile: da 6 a 15 cifre, con + davanti, spazi, trattini e parentesi ammessi. */
export function telefonoPlausibile(grezzo: string): boolean {
  if (!/^\+?[0-9\s().\-/]+$/.test(grezzo)) return false;
  const cifre = grezzo.replace(/\D/g, '');
  return cifre.length >= 6 && cifre.length <= 15;
}

export type CodiceErroreContatto = 'vuoto' | 'email_non_valida' | 'partita_iva_non_valida' | 'telefono_non_valido' | 'troppo_lungo';

/** Valuta UN campo dell'ultima schermata (la pagina lo usa campo per campo). */
export function valutaCampoContatto(
  campo: { id: CampoContattoId; obbligatorio: boolean },
  grezzo: unknown,
): { valore: string | null; errore?: CodiceErroreContatto } {
  const testo = typeof grezzo === 'string' ? grezzo.trim() : '';
  if (!testo) return campo.obbligatorio ? { valore: null, errore: 'vuoto' } : { valore: null };
  if (testo.length > LIMITI_QUESTIONARI.max_campo_contatto) return { valore: null, errore: 'troppo_lungo' };
  switch (campo.id) {
    case 'email':
      return normalizzaEmail(testo) ? { valore: testo } : { valore: null, errore: 'email_non_valida' };
    case 'partita_iva': {
      const p = normalizzaPartitaIva(testo);
      return p ? { valore: p } : { valore: null, errore: 'partita_iva_non_valida' };
    }
    case 'telefono':
      return telefonoPlausibile(testo) ? { valore: testo } : { valore: null, errore: 'telefono_non_valido' };
    default:
      return { valore: testo };
  }
}

export function validaContatti(
  schermata: SchermataContatti,
  input: Partial<Record<CampoContattoId, unknown>> | undefined,
): { contatti: ContattiRisposta; errori: Array<{ campo: CampoContattoId; codice: CodiceErroreContatto }> } {
  const contatti: ContattiRisposta = {};
  const errori: Array<{ campo: CampoContattoId; codice: CodiceErroreContatto }> = [];
  const dati = input ?? {};
  for (const campo of schermata.campi) {
    const esito = valutaCampoContatto(campo, dati[campo.id]);
    if (esito.errore) errori.push({ campo: campo.id, codice: esito.errore });
    else contatti[campo.id] = esito.valore;
  }
  return { contatti, errori };
}

/** Come si riconosce l'azienda già vista: prima la partita IVA, poi l'email. */
export type ChiaveLead = { campo: 'partita_iva'; valore: string } | { campo: 'email_normalizzata'; valore: string };

export function chiaveLead(contatti: ContattiRisposta): ChiaveLead | null {
  for (const campo of CAMPI_CHIAVE_LEAD) {
    if (campo === 'partita_iva') {
      const p = normalizzaPartitaIva(contatti.partita_iva ?? '');
      if (p) return { campo: 'partita_iva', valore: p };
    } else if (campo === 'email') {
      const e = normalizzaEmail(contatti.email ?? '');
      if (e) return { campo: 'email_normalizzata', valore: e };
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Piccoli aiuti per testi
// ─────────────────────────────────────────────────────────────────────────────

/** «simone@vivisolar.it» → «s•••••@vivisolar.it»: la pagina del collegamento non mostra mai l'indirizzo intero. */
export function mascheraEmail(email: string): string {
  const at = email.indexOf('@');
  if (at < 1) return '•••';
  const locale = email.slice(0, at);
  return `${locale.charAt(0)}${'•'.repeat(Math.max(3, Math.min(locale.length - 1, 8)))}${email.slice(at)}`;
}

/** Sostituisce `{nome}` e `{azienda}` nei testi dell'email; un segnaposto senza valore sparisce con lo spazio prima. */
export function sostituisciSegnaposti(testo: string, valori: { nome?: string | null; azienda?: string | null }): string {
  return testo
    .replace(/\s?\{nome\}/g, (m) => (valori.nome ? m.replace('{nome}', valori.nome) : ''))
    .replace(/\s?\{azienda\}/g, (m) => (valori.azienda ? m.replace('{azienda}', valori.azienda) : ''));
}
